import { useEffect, useState } from "react";
import ReactMakdown from "react-markdown"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { dracula } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";
import styled from "styled-components";
import { BtnHoverAnimation } from "../animes";
import TagSearch from "./TagSearch";
import { Tag } from "../public/Tags";
import Search from "../public/Search";
import { modifyContent, newContent } from "../api/contents";
import { getDate } from "../public/getDate";
import { useNavigate, useParams } from "react-router-dom";
import ImgUploader from "./ImageUploader";
import { ContentIF, ImageIF, TagIF } from "../types";
import axios from "axios";
import { TagIcon, TagIconContainer } from "../icons";
import Header from "../public/Header";
import Copyright from "../public/Copyright";

const HEADER_HEIGHT = 60;
const HEADER_BOTTOM = 30;

 /* Writing.tsx는 게시물을 작성할 수 있는 페이지 컴포넌트 */

// 마크다운 스타일
export const MdExtra = styled.div`

    /* 테이블 스타일 */
    table {
    border: 1px ${props => props.theme.MARKDOWN.TABLE.BORDER} solid;
    font-size: .9em;
    box-shadow: 0 2px 5px ${props => props.theme.MARKDOWN.TABLE.SHADOW};
    width: 70%;
    border-collapse: collapse;
    border-radius: 5px;
    overflow: hidden;
    margin: 2rem auto;
    }

    th {
    text-align: left;
    }
    
    thead {
    font-weight: bold;
    color: ${props => props.theme.MARKDOWN.THEAD.FONT_COLOR};
    background: ${props => props.theme.MARKDOWN.THEAD.BACKGROUND};
    }
    
    td, th {
    padding: 1em .5em;
    vertical-align: middle;
    }
    
    td {
    border-bottom: 1px solid ${props => props.theme.MARKDOWN.TD.BORDER};
    background: ${props => props.theme.MARKDOWN.TD.BACKGROUND};
    }

    /* 인용문 스타일 */

    blockquote {
        display:block;
        background: ${props => props.theme.MARKDOWN.BLOCKQUOTE.BACKGROUND};
        padding: 15px 20px 15px 45px;
        margin: 2rem auto;
        width: 70%;
        position: relative;
        
        /*Font*/
        font-family: Georgia, serif;
        font-size: 20px;
        font-weight: 600;
        line-height: 1.2;
        color: ${props => props.theme.MARKDOWN.BLOCKQUOTE.FONT_COLOR};

        /*Box Shadow - (Optional)*/
        -moz-box-shadow: 2px 2px 15px ${props => props.theme.MARKDOWN.BLOCKQUOTE.SHADOW};
        -webkit-box-shadow: 2px 2px 15px ${props => props.theme.MARKDOWN.BLOCKQUOTE.SHADOW};
        box-shadow: 2px 2px 15px ${props => props.theme.MARKDOWN.BLOCKQUOTE.SHADOW};

        /*Borders - (Optional)*/
        border-left-style: solid;
        border-left-width: 15px;
        border-right-style: solid;
        border-right-width: 2px;  
    }
`;

const BG = styled.div`
  height: 100vh;                 /* 🔥 전체 화면 고정 */
  display: flex;
  flex-direction: column;
  overflow: hidden;              /* 🔥 전체 스크롤 막기 */
  background: linear-gradient(135deg, ${props => props.theme.ROOT_BACKGROUND.FIRST} 0%, ${props => props.theme.ROOT_BACKGROUND.LAST} 100%);
  font-family: 'Pretendard', -apple-system, sans-serif;
`;

const Body = styled.div`
  width: 100%;
  margin: 0 auto;
  gap: 2rem;

  padding-top: ${HEADER_BOTTOM}px;
  margin-top: ${HEADER_HEIGHT}px;  /* 🔥 헤더만큼 밀기 */
  overflow-y: scroll;
`;

const Box = styled.section`
    background-color: ${props => props.theme.BOX_BACKGROUND};
    width: 80rem;
    margin: 0 auto 2rem auto;
    padding: 1rem 2rem;

    border: 1px solid ${props => props.theme.BOX_BORDER};
    border-radius: 7px; 
    box-shadow: 0 4px 6px -1px ${props => props.theme.BOX_SHADOW.FIRST}, 0 2px 4px -1px ${props => props.theme.BOX_SHADOW.LAST};
`;

/* Title */

const TitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const Subtitle = styled.span`
    font-size: 1.1rem;
    font-weight: 700;
    color: ${props => props.theme.MENU.SUBTITLE_COLOR};
    width: 7.7rem;

    margin-right: 10rem;
`;

/* tags */

const TagsContainer = styled(Box)`
        & > span {
        display: flex;
        align-items: center;
        gap: 8.5rem;
    }
`;

const SectionTitle = styled.h2`
    font-size: 1rem;
    font-weight: 700;
    margin: 1.5rem 6rem 0.5rem 0;
    color: ${props => props.theme.MENU.SUBTITLE_COLOR};
`;

const TagList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
`;

/* Img */

const ImgContainer = styled(Box)``;

/* post */

const PostContainer = styled(Box)`
    & > span {
        display: flex;
    }

    margin-bottom: 5rem;
    padding-bottom: 4rem;
`;

const EditorContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;

    & > ${Subtitle} {
        margin-bottom: 2rem;
    }
`;

const PreviewContainer = styled(MdExtra)`
    width: 70rem;
    min-height: 20rem;

    margin: 0 4rem 3rem 4rem;
    padding: 1rem;

    overflow-y: auto;

    background-color: ${props => props.theme.BOX_BACKGROUND};
    color: ${props => props.theme.CONTENT.TITLE_COLOR};
    border: 0.1rem solid ${props => props.theme.MENU.ITEM_COLOR};
    border-radius: 10px;
`;

const PostInput = styled.textarea.attrs({placeholder: "내용을 입력하세요..."})`
    width: 70rem;
    min-height: 20rem;
    margin: 3rem 4rem;
    padding: 1rem;
    
    font-size: 15px;
    font-family: inherit;
    background-color: ${props => props.theme.BOX_BACKGROUND};
    color: ${props => props.theme.CONTENT.TITLE_COLOR};
    border: 0.1rem solid ${props => props.theme.MENU.ITEM_COLOR};
    border-radius: 10px;

    &::placeholder {
        color: ${props => props.theme.CONTENT.TITLE_COLOR};
    }
`;

const Btn = styled.button`
    width: 5rem;
    height: 2.5rem;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 0.5rem;

    float: right;
    color: white;

    letter-spacing: -1px;
    font-weight: 600;

    &:hover {
        animation: ${props => BtnHoverAnimation(props.theme.BTN_ENTER.HOVER.BACKGROUND.FIRST, props.theme.BTN_ENTER.HOVER.BACKGROUND.LAST)} 0.3s;
        background-color: ${props => props.theme.BTN_ENTER.HOVER.BACKGROUND.LAST};
        box-shadow: inset 0 6px 6px ${props => props.theme.BTN_ENTER.HOVER.SHADOW};
    }

    &:not(:hover) {
        animation: ${props => BtnHoverAnimation(props.theme.BTN_ENTER.HOVER.BACKGROUND.LAST, props.theme.BTN_ENTER.HOVER.BACKGROUND.FIRST)} 0.3s;
        background-color: ${props => props.theme.BTN_ENTER.HOVER.BACKGROUND.FIRST};
    }
`;

function Writing() {
    const navigate  = useNavigate();
    const { id } = useParams<{id: string}>();
    const prev_id = Number(id); // 기존 콘텐츠의 id (수정이 아닌 새 콘텐츠 작성 시에는 undefined);

    const [title, setTitle] = useState("");
    const [summary, setSummary] = useState("");
    const [post, setPost] = useState("");

    const [searchedTag, setSearchedTag] = useState("");
    const [tags, setTags] = useState<TagIF[]>([]);
    const [selectedTags, setSelectedTags] = useState<TagIF[]>([]);
    const [images, setImages] = useState<ImageIF[]>([]);


    useEffect(() => {
        // modify로 접근한 경우 기존 콘텐츠 정보 불러오기
        if (prev_id) {
            axios.get(`${process.env.REACT_APP_API_URL}/contents/getContents`)
            .then(res => {
                res.data.forEach((content: ContentIF) => {
                    if (content.id === prev_id) {
                        setTitle(content.title === "제목 없음" ? "" : content.title);
                        setSummary(content.summary);
                        setPost(content.post);
                        setSelectedTags(content.tags);
                        const prevImages = content.images.map((path: string) => ({
                            file: null,              // 🔥 기존 이미지는 file 필요 없음
                            preview: path            // 🔥 상대경로 그대로 유지
                        }));
                        setImages(prevImages);
                }})
            }).catch(err => console.error(err));
        }

        axios.get(`${process.env.REACT_APP_API_URL}/tags/getTags`)
        .then(res => {
            const _tags = res.data;
            setTags(_tags);
        })
        .catch(err => console.error(err));
    }, []);

    // 검색한 태그의 후보군
    const searchedTags = searchedTag
    ? tags.filter(
        tag =>
            tag.name.toUpperCase()
            .includes(searchedTag.toUpperCase()) &&
            !selectedTags.some(t => t.id === tag.id)
        )
    : [];

    // 그 외의 후보군
    const unsearchedTags = searchedTag
    ? tags.filter(
        tag =>
            !tag.name.toUpperCase()
            .includes(searchedTag.toUpperCase()) &&
            !selectedTags.some(t => t.id === tag.id)
        )
    : tags.filter(
        tag => !selectedTags.some(t => t.id === tag.id)
        );
    
    // 태그 선택 toggle event
    const select = (tag: TagIF) => {
    setSelectedTags(prev =>
        prev.some(t => t.id === tag.id) 
        ? prev.filter(t => t.id !== tag.id) // 이미 선택한 태그라면 제외
        : [...prev, tag] // 선택하지 않았다면 포함
    );};

    // 태그 선택 확인 여부
    const isSelected = (tag: TagIF) =>
        selectedTags.some(t => t.id === tag.id);

    // 업로드한 사진 클릭 시 post에 사진 삽입
    const insertImageToPost = (index: number) => {
        // post에 ![img0] 꼴로 삽입
        const path = images[index].preview.includes(process.env.REACT_APP_API_URL) 
                    ? images[index].preview.replace(process.env.REACT_APP_API_URL, "") 
                    : images[index].preview;
        setPost(prev => prev + `\n\n![image](${path})\n\n`);
    }

    // 업로드한 사진 삭제
    const delImageFromPost = (index: number) => {
        const path = images[index].preview.includes(process.env.REACT_APP_API_URL) 
                    ? images[index].preview.replace(process.env.REACT_APP_API_URL, "") 
                    : images[index].preview;
        setPost(prev => prev.replaceAll(`![image](${path})`, ""));
    }

    // 콘텐츠 제출 시 event (서버로 전달)
    const submitContent = async () => {
        try {
            if (prev_id) {
                const { year, month, day, hour, min, sec } = getDate();
                await modifyContent(
                    prev_id,
                    title === "" ? "제목 없음" : title ,
                    post,
                    summary,
                    `${year}-${month}-${day} ${hour}:${min}:${sec}`,
                    selectedTags,
                    images
                );
                navigate(`/content/${prev_id}`);
            } else {
                const { year, month, day, hour, min, sec } = getDate();
                const new_id = Date.now();
                await newContent(
                new_id, 
                title === "" ? "제목 없음" : title , 
                post, 
                summary,
                `${year}-${month}-${day} ${hour}:${min}:${sec}`,
                selectedTags,
                images);
                
                navigate(`/content/${new_id}`);
            }

        } catch (error) {
            alert("콘텐츠 제출에 실패했습니다. 다시 시도해주세요.");
            console.log(error);
        }
    }

    const Code = styled.code`
        box-shadow: 0 2px 5px ${props => props.theme.MARKDOWN.CODE.SHADOW};
    `;

    return (<BG>
        <Header title="Lamiro's writing"/>
        <Body>
            <Box>
                <TitleContainer>
                    <Subtitle>Title</Subtitle>
                    <Search
                        searchedTerm={title}
                        onSearchedTerm={setTitle}
                        styleFor={process.env.REACT_APP_STYLE_WRITING_SEARCH}
                        placeholder={"제목을 입력하세요..."}
                    />
                </TitleContainer>
                <TitleContainer style={{marginTop: "1rem"}}>
                    <Subtitle>Summary</Subtitle>
                    <Search
                        searchedTerm={summary}
                        onSearchedTerm={setSummary}
                        styleFor={process.env.REACT_APP_STYLE_WRITING_SEARCH}
                        placeholder={"요약을 입력하세요..."}
                    />
                </TitleContainer>
            </Box>

            <TagsContainer>
                <span>
                    <SectionTitle>Tags</SectionTitle>
                    <TagSearch 
                            searchedTerm={searchedTag}
                            onSearchedTerm={setSearchedTag}
                            tags={tags}
                            onTags={setTags}
                    />
                </span>

                <SectionTitle>Selected</SectionTitle>
                <TagList>
                    {selectedTags.map(tag => (
                        <Tag key={tag.id}
                            onClick={() => select(tag)}
                            $selected>
                            <TagIconContainer>
                                <TagIcon />
                                {tag.name}
                            </TagIconContainer>
                        </Tag>
                    ))}
                </TagList>

                <SectionTitle>Searched</SectionTitle>
                <TagList>
                    {searchedTags.map(tag => (
                        <Tag key={tag.id}
                            onClick={() => select(tag)}
                            $selected={isSelected(tag)}
                            $searched>
                            <TagIconContainer>
                                <TagIcon />
                                {tag.name}
                            </TagIconContainer>
                        </Tag>
                    ))}
                </TagList>
                
                <SectionTitle>Others</SectionTitle>
                <TagList>
                    {unsearchedTags.map(tag => (
                        <Tag key={tag.id}
                            onClick={() => select(tag)}
                            $selected={isSelected(tag)}>
                            <TagIconContainer>
                                <TagIcon />
                                {tag.name}
                            </TagIconContainer>
                        </Tag>
                    ))}
                </TagList>
            </TagsContainer>

            <ImgContainer>
                    <Subtitle>Img</Subtitle>
                    <ImgUploader images={images} onImages={setImages} onImageClick={insertImageToPost} onDelClick={delImageFromPost}/>
            </ImgContainer>

            <PostContainer>
                <Subtitle>Post</Subtitle>
                <EditorContainer>
                    <PostInput value={post} onChange={((e) => setPost(e.target.value))}/>
                    <Subtitle>Preview</Subtitle>
                    <PreviewContainer>
                        <ReactMakdown urlTransform={(uri) => uri.startsWith('/database')
                            ? `${process.env.REACT_APP_API_URL}${uri}` : uri}
                            remarkPlugins={[remarkGfm]}
                            components={{
                                code(props) {
                                    const { className, style, children, ...rest } = props;
                                    const match = /language-(\w+)/.exec(className || '');
                                    return match ? (
                                        <SyntaxHighlighter
                                        style={dracula}
                                        language={match[1]}
                                        PreTag="div"
                                    >
                                        {String(children).replace(/\n$/, '')}
                                        </SyntaxHighlighter>) : (
                                        <Code className={className} {...rest}>
                                            {children}
                                        </Code>
                                    );
                                },
                            img: ({node, ...props}) => <img {...props} style={{maxWidth: '80%', borderRadius: "7px", display: 'block', marginLeft: 'auto', marginRight: 'auto'}} alt=""/>
                            }}>{post}
                        </ReactMakdown>
                    </PreviewContainer>
                </EditorContainer>
                <Btn type="button" onClick={submitContent}>Submit</Btn>
            </PostContainer>
            <Copyright />
        </Body>
    </BG>
    );
}

export default Writing;
